/**
 * ApprovalHistoryTable - Table component for displaying manager approval/rejection audit trail
 * Shows on-hold inventory items that have been approved or rejected by a manager,
 * with date range, action type, and text search filters.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx-js-style';
import { useLanguage } from '../../../i18n/LanguageContext';
import { inventoryService, ApprovalHistoryRecord } from '../../../services/inventoryService';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import {
  Calendar,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Filter,
  Download,
  Printer,
} from 'lucide-react';

const ApprovalHistoryTable: React.FC = () => {
  const { t } = useLanguage();

  // Data state
  const [records, setRecords] = useState<ApprovalHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounce search text (500ms delay)
  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [searchText]);

  // Fetch approval history from API
  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.start_date = dateFrom;
      if (dateTo) params.end_date = dateTo;
      if (actionFilter && actionFilter !== 'all') params.approval_status = actionFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

      const data = await inventoryService.getApprovalHistory(params);
      setRecords(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, actionFilter, debouncedSearch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Clear filters
  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setActionFilter('all');
    setSearchText('');
  };

  // Format hold reason using i18n keys, handling null/undefined values
  const formatHoldReason = (reason: string | null | undefined): string => {
    if (!reason) return '-';
    const key = `approvalHistory.holdReasons.${reason}` as any;
    const translated = t(key);
    // If translation key is returned as-is, fallback to capitalized reason
    if (translated === key || !translated) {
      return reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
    return translated;
  };

  // Export to Excel
  const exportToExcel = () => {
    const headers = [
      t('approvalHistory.date'),
      t('approvalHistory.action'),
      t('approvalHistory.manager'),
      t('approvalHistory.itemNumber'),
      t('approvalHistory.itemName'),
      t('approvalHistory.quantity'),
      t('approvalHistory.weight'),
      t('approvalHistory.transferNumber'),
      t('approvalHistory.receivedBy'),
      t('approvalHistory.receivedAt'),
      t('approvalHistory.holdReason'),
      t('approvalHistory.notes'),
    ];

    const rows = records.map(r => [
      new Date(r.approval_date).toLocaleString(),
      r.approval_status === 'approved' ? t('approvalHistory.approved') : t('approvalHistory.rejected'),
      r.manager_name,
      r.item_number,
      (r.item_english_name || '') + (r.item_chinese_name ? ` (${r.item_chinese_name})` : ''),
      r.actual_quantity,
      Number(r.actual_weight).toFixed(2),
      r.transfer_number,
      r.verified_by_name || '',
      r.verified_at ? new Date(r.verified_at).toLocaleString() : new Date(r.received_date).toLocaleDateString(),
      formatHoldReason(r.on_hold_reason),
      r.approval_notes || '',
    ]);

    const wsData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    const headerStyle = {
      fill: { fgColor: { rgb: '2563EB' } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center' as const, vertical: 'center' as const },
    };

    headers.forEach((_, colIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex });
      if (ws[cellRef]) ws[cellRef].s = headerStyle;
    });

    ws['!cols'] = headers.map((header, colIndex) => {
      let maxLength = header.length;
      rows.forEach(row => {
        const cellLength = String(row[colIndex]).length;
        if (cellLength > maxLength) maxLength = cellLength;
      });
      return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Approval History');
    XLSX.writeFile(wb, `approval-history-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Print report
  const handlePrint = () => {
    if (!records.length) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const tableRows = records.map(r => `
      <tr>
        <td>${new Date(r.approval_date).toLocaleString()}</td>
        <td><span style="color: ${r.approval_status === 'approved' ? '#15803d' : '#b91c1c'}; font-weight: 600;">${r.approval_status === 'approved' ? t('approvalHistory.approved') : t('approvalHistory.rejected')}</span></td>
        <td>${r.manager_name}</td>
        <td>${r.item_number}</td>
        <td>${(r.item_english_name || '-') + (r.item_chinese_name ? ` (${r.item_chinese_name})` : '')}</td>
        <td style="text-align:right">${r.actual_quantity}</td>
        <td style="text-align:right">${Number(r.actual_weight).toFixed(2)} lb</td>
        <td>${r.transfer_number}</td>
        <td>${r.verified_by_name || '-'}</td>
        <td>${r.verified_at ? new Date(r.verified_at).toLocaleString() : new Date(r.received_date).toLocaleDateString()}</td>
        <td>${formatHoldReason(r.on_hold_reason)}</td>
        <td>${r.approval_notes || '-'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t('approvalHistory.title')} - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 10px; }
          .summary { margin-bottom: 15px; font-size: 14px; }
          table { border-collapse: collapse; width: 100%; font-size: 11px; }
          th { background-color: #e5e7eb; color: #111; padding: 10px 8px; text-align: left; border: 1px solid #d1d5db; font-weight: 600; }
          td { border: 1px solid #ddd; padding: 8px; }
          tr:nth-child(even) { background-color: #f9fafb; }
          @media print { body { margin: 0; padding: 10px; } h1 { font-size: 14px; } }
        </style>
      </head>
      <body>
        <h1>${t('approvalHistory.title')} - ${new Date().toLocaleDateString()}</h1>
        <div class="summary"><strong>Total Records:</strong> ${records.length}</div>
        <table>
          <thead>
            <tr>
              <th>${t('approvalHistory.date')}</th>
              <th>${t('approvalHistory.action')}</th>
              <th>${t('approvalHistory.manager')}</th>
              <th>${t('approvalHistory.itemNumber')}</th>
              <th>${t('approvalHistory.itemName')}</th>
              <th>${t('approvalHistory.quantity')}</th>
              <th>${t('approvalHistory.weight')}</th>
              <th>${t('approvalHistory.transferNumber')}</th>
              <th>${t('approvalHistory.receivedBy')}</th>
              <th>${t('approvalHistory.receivedAt')}</th>
              <th>${t('approvalHistory.holdReason')}</th>
              <th>${t('approvalHistory.notes')}</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const hasActiveFilters = dateFrom || dateTo || actionFilter !== 'all' || searchText;

  return (
    <div className="flex flex-col">
      {/* Header with filters and refresh */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Filter className="w-4 h-4 text-gray-500" />
            {t('common.filter')}
            {hasActiveFilters && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[dateFrom, dateTo, actionFilter !== 'all' ? actionFilter : '', searchText].filter(Boolean).length}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 px-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <span className="text-gray-400">&times;</span>
              {t('common.clear')}
            </button>
          )}

          <button
            onClick={fetchHistory}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            title={t('common.reset')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Record count */}
          {!loading && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {records.length} {records.length === 1 ? 'record' : 'records'}
            </span>
          )}

          <button
            onClick={handlePrint}
            disabled={records.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.print')}</span>
          </button>
          <button
            onClick={exportToExcel}
            disabled={records.length === 0}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t('common.export')}</span>
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilterPanel && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('approvalHistory.dateFrom')}
              </label>
              <DatePickerDropdown
                selected={dateFrom || null}
                onSelect={(date) => setDateFrom(date)}
              >
                {({ onClick }) => (
                  <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                    {dateFrom ? (() => { const d = new Date(dateFrom + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                )}
              </DatePickerDropdown>
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('approvalHistory.dateTo')}
              </label>
              <DatePickerDropdown
                selected={dateTo || null}
                onSelect={(date) => setDateTo(date)}
              >
                {({ onClick }) => (
                  <button type="button" onClick={onClick} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                    {dateTo ? (() => { const d = new Date(dateTo + 'T00:00:00'); return `${d.toLocaleDateString('en-US', { weekday: 'short' })} — ${d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}`; })() : 'Select date...'}
                    <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                  </button>
                )}
              </DatePickerDropdown>
            </div>

            {/* Action Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('approvalHistory.actionFilter')}
              </label>
              <select
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              >
                <option value="all">{t('approvalHistory.all')}</option>
                <option value="approved">{t('approvalHistory.approved')}</option>
                <option value="rejected">{t('approvalHistory.rejected')}</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Search className="w-4 h-4 inline mr-1" />
                {t('common.search')}
              </label>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('approvalHistory.searchPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-8 text-center text-gray-500 dark:text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          {t('common.loading')}
        </div>
      )}

      {/* Empty state */}
      {!loading && records.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-8 text-center">
          <CheckCircle className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-500 dark:text-gray-400 font-medium">{t('approvalHistory.noRecords')}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{t('approvalHistory.noRecordsDesc')}</p>
        </div>
      )}

      {/* Card Layout */}
      {!loading && records.length > 0 && (
        <div className="space-y-3">
          {records.map((record) => {
            const isApproved = record.approval_status === 'approved';
            return (
              <div
                key={record.id}
                className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden border-l-4 ${
                  isApproved
                    ? 'border-l-green-500'
                    : 'border-l-red-500'
                }`}
              >
                <div className="p-4">
                  {/* Top row: Badge + date + manager */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {isApproved ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                          <CheckCircle className="w-3 h-3" />
                          {t('approvalHistory.approved')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">
                          <XCircle className="w-3 h-3" />
                          {t('approvalHistory.rejected')}
                        </span>
                      )}
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {t('approvalHistory.manager')}: <span className="text-gray-900 dark:text-white font-medium">{record.manager_name}</span>
                      </span>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(record.approval_date).toLocaleString()}
                    </span>
                  </div>

                  {/* Line 1: Item #, Item Name, Qty, Weight */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.itemNumber')}:</span>
                      <span className="ml-1.5 font-mono font-medium text-gray-900 dark:text-white">{record.item_number}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.itemName')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{record.item_english_name || '-'}{record.item_chinese_name ? ` (${record.item_chinese_name})` : ''}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.quantity')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{record.actual_quantity}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.weight')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{Number(record.actual_weight).toFixed(2)} lb</span>
                    </div>
                  </div>

                  {/* Line 2: Transfer #, Received By, Hold Reason */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mt-1">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.transferNumber')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{record.transfer_number}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.receivedBy')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{record.verified_by_name || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.receivedAt')}:</span>
                      <span className="ml-1.5 text-gray-900 dark:text-white">{record.verified_at ? new Date(record.verified_at).toLocaleString() : new Date(record.received_date).toLocaleDateString()}</span>
                    </div>
                    {record.on_hold_reason && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{t('approvalHistory.holdReason')}:</span>
                        <span className="ml-1.5 text-gray-900 dark:text-white">{formatHoldReason(record.on_hold_reason)}</span>
                      </div>
                    )}
                  </div>

                  {/* Notes (only when present) */}
                  {record.approval_notes && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('approvalHistory.notes')}</span>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{record.approval_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalHistoryTable;
